import React from 'react';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Image from '@lib/components/Image/Image';
import downloadHandler from '@lib/utils/downloadHandler';
import useArrowKeyNavigation from '@lib/hooks/useArrowKeyNavigation';
import styles from './ImageViewer.module.scss';

interface Props {
  fileUrl: string;
  downloadUrl: string;
  onFullScreenToggle?: () => void;
  isFullScreen?: string;
  swiperProps?: {
    onPrevious?: () => void;
    onNext?: () => void;
    isPreviousDisabled?: boolean;
    isNextDisabled?: boolean;
  };
  useDownloadTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
}

export default function ImageViewer(props: Props) {
  const {
    fileUrl,
    downloadUrl,
    onFullScreenToggle,
    isFullScreen,
    swiperProps = {},
    useDownloadTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
  } = props;

  const { onPrevious, onNext, isPreviousDisabled, isNextDisabled } =
    swiperProps;

  useArrowKeyNavigation([
    {
      keys: 'ArrowLeft',
      handler: () => onPrevious?.(),
      disabled: !onPrevious || !!isPreviousDisabled,
    },
    {
      keys: 'ArrowRight',
      handler: () => onNext?.(),
      disabled: !onNext || !!isNextDisabled,
    },
  ]);

  // Opens a new tab instead of printing via a hidden iframe: matches
  // PDFViewer's printFile, whose iframe-based print silently does nothing
  // for Chrome's built-in PDF viewer (see PDFViewer.tsx). Printing an <img>
  // in a hidden iframe still works fine on its own, but keeping both
  // viewers' print actions on the same mechanism means this doesn't quietly
  // rot to a different (untested) failure mode as browsers change.
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      console.warn('handlePrint: print window was blocked');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <style>
            @media print {
              body {
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @page {
                size: auto;
                margin: 0;
              }
              header, footer {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <img src="${fileUrl}" alt="Image for print" />
        </body>
      </html>
      `);
    printWindow.document.close();

    const img = printWindow.document.querySelector('img');
    img?.addEventListener('load', () => {
      printWindow.focus();
      printWindow.print();
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.actionsRow}>
          {onFullScreenToggle && (
            <IconButton
              icon={isFullScreen ? 'fullscreen_exit' : 'fullscreen'}
              onClick={onFullScreenToggle}
              symbolsOutlined
            />
          )}
          {onPrevious && (
            <IconButton
              icon="chevron_left"
              onClick={onPrevious}
              disabled={isPreviousDisabled}
              symbolsOutlined
            />
          )}
          {onNext && (
            <IconButton
              icon="chevron_right"
              onClick={onNext}
              disabled={isNextDisabled}
              symbolsOutlined
            />
          )}
        </div>
        <div className={styles.actionsRow}>
          <IconButton icon="print" onClick={handlePrint} symbolsOutlined />
          {useDownloadTool && (
            <IconButton
              icon="download"
              onClick={() => downloadHandler(downloadUrl)}
              symbolsOutlined
            />
          )}
          {useFavoriteTool && onFavoriteToggle && (
            <IconButton
              icon={isFavorited ? 'bookmark_star' : 'bookmark'}
              onClick={onFavoriteToggle}
              symbolsOutlined
              filled={isFavorited}
              isLoading={isFavoriteLoading}
              type={IconButtonTypes.PrimaryOutlined}
            />
          )}
        </div>
      </div>
      <div className={styles.imageWrap}>
        <Image src={fileUrl} alt="Image" className={styles.image} />
      </div>
    </div>
  );
}
